import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { articles } from '../data/articles';
import ArticleCard from './ArticleCard';
import {
  useCurrentLang,
  fetchArticleMetadata,
  type ArticleMetadata,
} from '../hooks/useArticleMetadata';

const FeaturedArticles: React.FC = () => {
  const { t } = useTranslation();
  const currentLang = useCurrentLang();
  const [featuredArticles, setFeaturedArticles] = useState<ArticleMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedArticles = async () => {
      try {
        const recentArticles = [...articles].slice(0, 3);

        const metadataPromises = recentArticles.map((article) =>
          fetchArticleMetadata(article.slug, currentLang)
        );

        const results = await Promise.all(metadataPromises);
        const articlesData = results.filter(
          (m): m is ArticleMetadata => m !== undefined
        );
        setFeaturedArticles(articlesData);
      } catch (error) {
        console.error('Error loading featured articles:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedArticles();
  }, [currentLang]);

  return (
    <div className="mb-32">
      <div className="flex justify-between items-center">
        <h2>{t('articles.title')}</h2>
        <Link to={`/${currentLang}/articles`} className="text-primary hover:underline flex items-center">
          {t('articles.viewAll')}
          <ArrowRight size={16} className="ml-1" aria-hidden="true" />
        </Link>
      </div>
      <p className="max-w-4xl mb-12">
        {t('articles.description')}
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {isLoading ? (
          // Loading skeletons
          Array(3).fill(0).map((_, index) => (
            <div key={index} className="bg-white rounded-card shadow-lg overflow-hidden animate-pulse">
              <div className="h-48 bg-gray-200"></div>
              <div className="p-6">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
                <div className="flex gap-2">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))
        ) : (
          featuredArticles.map(article => (
            <ArticleCard 
              key={article.id} 
              article={article} 
              metadata={article} // Pass the pre-fetched metadata
            />
          ))
        )}
      </div>
    </div>
  );
};

export default FeaturedArticles;